import React from 'react';
import Icon from '../../../components/AppIcon';


const EnvironmentSwitcher = ({ currentEnvironment, onEnvironmentChange, environments }) => {
  const getEnvironmentIcon = (env) => {
    switch (env) {
      case 'production':
        return 'Globe';
      case 'sandbox':
        return 'TestTube';
      case 'development':
        return 'Code';
      default:
        return 'Settings';
    }
  };

  const getEnvironmentColor = (env) => {
    switch (env) {
      case 'production':
        return 'text-success';
      case 'sandbox':
        return 'text-warning';
      case 'development':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getEnvironmentBg = (env, isActive) => {
    if (!isActive) return 'bg-muted hover:bg-muted/80';
    
    switch (env) {
      case 'production':
        return 'bg-success/10 border-success/20';
      case 'sandbox':
        return 'bg-warning/10 border-warning/20';
      case 'development':
        return 'bg-primary/10 border-primary/20';
      default:
        return 'bg-muted';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-card-foreground">Environment</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Switch between different deployment environments
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse-soft" />
          <span className="text-sm font-medium text-success">Active</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {environments?.map((env) => {
          const isActive = currentEnvironment === env.key;
          
          return (
            <button
              key={env.key}
              onClick={() => onEnvironmentChange(env.key)}
              className={`relative p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                isActive 
                  ? `${getEnvironmentBg(env.key, true)} border-current` 
                  : 'bg-muted hover:bg-muted/80 border-transparent hover:border-border'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isActive ? 'bg-background/50' : 'bg-background'
                }`}>
                  <Icon 
                    name={getEnvironmentIcon(env.key)} 
                    size={20} 
                    className={isActive ? getEnvironmentColor(env.key) : 'text-muted-foreground'}
                  />
                </div>
                {isActive && (
                  <div className="w-3 h-3 bg-current rounded-full animate-pulse-soft" />
                )}
              </div>

              <h3 className={`font-semibold mb-1 ${
                isActive ? 'text-card-foreground' : 'text-card-foreground'
              }`}>
                {env.name}
              </h3>
              
              <p className={`text-sm mb-3 ${
                isActive ? 'text-muted-foreground' : 'text-muted-foreground'
              }`}>
                {env.description}
              </p>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Endpoint:</span>
                  <span className={`font-mono ${
                    isActive ? 'text-card-foreground' : 'text-muted-foreground'
                  }`}>
                    {env.endpoint}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Status:</span>
                  <div className="flex items-center space-x-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      env.status === 'healthy' ? 'bg-success' : 
                      env.status === 'warning' ? 'bg-warning' : 'bg-destructive'
                    }`} />
                    <span className={`capitalize ${
                      env.status === 'healthy' ? 'text-success' : 
                      env.status === 'warning' ? 'text-warning' : 'text-destructive'
                    }`}>
                      {env.status}
                    </span>
                  </div>
                </div>
              </div>

              {isActive && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer rounded-lg" />
              )}
            </button>
          );
        })}
      </div>
      {/* Environment Details */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Active Connections:</span>
            <p className="font-medium text-card-foreground">
              {environments?.find(env => env.key === currentEnvironment)?.activeConnections || 0}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Data Sync:</span>
            <p className="font-medium text-card-foreground">
              {environments?.find(env => env.key === currentEnvironment)?.lastSync || 'Never'}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">API Calls:</span>
            <p className="font-medium text-card-foreground">
              {environments?.find(env => env.key === currentEnvironment)?.apiCalls?.toLocaleString() || '0'}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Uptime:</span>
            <p className="font-medium text-card-foreground">
              {environments?.find(env => env.key === currentEnvironment)?.uptime || '99.9%'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentSwitcher;